import React from 'react';
import { cn } from '@/lib/utils';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Inbox } from 'lucide-react';

/**
 * Represents the shape of a React Query result object.
 * We accept a subset so we don't couple to a specific TanStack Query version.
 */
interface QueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface QueryPageProps<T> {
  /** The React Query result object */
  query: QueryResult<T>;
  /** Message shown when data is an empty array */
  emptyMessage?: string;
  /** Optional action rendered in the empty state (e.g. a "Create" button) */
  emptyAction?: React.ReactNode;
  /** Optional icon for the empty state */
  emptyIcon?: React.ReactNode;
  /** Optional text shown while loading */
  loadingText?: string;
  /** Additional CSS classes for the wrapper div */
  className?: string;
  /** Render function called with the data when the query succeeds and data is non-empty */
  children: (data: T) => React.ReactNode;
}

/**
 * QueryPage — Wraps a React Query result and automatically handles loading,
 * error, and empty states. The developer only writes the success case.
 *
 * @example
 * ```tsx
 * <QueryPage
 *   query={useWorkItems()}
 *   emptyMessage="No work items yet"
 *   emptyAction={<Button onClick={openCreate}>Create Work Item</Button>}
 * >
 *   {(data) => <WorkItemTable items={data} />}
 * </QueryPage>
 * ```
 */
export function QueryPage<T>({
  query,
  emptyMessage = 'No items found',
  emptyAction,
  emptyIcon,
  loadingText,
  className,
  children,
}: QueryPageProps<T>) {
  const { data, isLoading, isError, error, refetch } = query;

  // Loading state
  if (isLoading) {
    return (
      <div className={cn(className)}>
        <LoadingState text={loadingText} />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className={cn('py-8', className)}>
        <Alert variant="error" title="Something went wrong">
          <p className="mb-3">
            {error?.message || 'An unexpected error occurred while loading data.'}
          </p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  // Empty state — only applies when data is an array
  if (Array.isArray(data) && data.length === 0) {
    return (
      <div className={cn(className)}>
        <EmptyState
          icon={emptyIcon || <Inbox className="h-8 w-8" />}
          title={emptyMessage}
        />
        {emptyAction && (
          <div className="flex justify-center mt-4">
            {emptyAction}
          </div>
        )}
      </div>
    );
  }

  // No data at all (undefined/null after loading)
  if (data === undefined || data === null) {
    return (
      <div className={cn(className)}>
        <EmptyState
          icon={emptyIcon || <Inbox className="h-8 w-8" />}
          title={emptyMessage}
        />
      </div>
    );
  }

  // Success state
  return <div className={cn(className)}>{children(data)}</div>;
}
